/* eslint-disable no-param-reassign */
import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import {
  Table as AntTable,
  Row,
  Col,
  Button,
  Menu,
  Input,
  Dropdown,
  Popover,
  Typography,
  Grid,
  Form,
  Modal,
  Spin,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  SearchOutlined,
  CloseOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SettingOutlined,
  TableOutlined,
  ArrowLeftOutlined,
  ExpandOutlined,
  DownOutlined,
  RightOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';
import API, { baseURL } from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import Dragger from 'components/Dragger';
import FormSubmitControls from 'components/FormSubmitControls';
import { hasActionPermission } from 'utils/permission/index';
import { logger } from 'utils/debug';
import useScreen from 'hooks/useScreen';
import Pagination from './Pagination';
import Select from './Select';

const { Text } = Typography;

const Table = ({
  cols: _cols,
  mobileCols: __mobileCols,
  data,
  rowSelection,
  onRow: _onRow,
  editText,
  noControls,
  controls,
  allowEdit: _allowEdit,
  editDisabled,
  editDisabledTooltip,
  allowAdd: _allowAdd,
  allowDelete: _allowDelete,
  allowSearch,
  allowSort,
  allowConfig,
  allowExpand: _allowExpand,
  expandable,
  filterNested,
  childrenProp,
  rowKey,
  customActions,
  handleOnRowClick,
  mobileColIndex: _mobileColIndex,
  pageSize,
  disabled,
  allowColumnSelector,
  baseURI,
  permission,
  hasAllViewPermission: _hasAllViewPermission,
  pagination,
  paginationCallback,
  count,
  currentPage,
  defaultVisibleSearch,
  loading: _loadingTable,
  restHeigth,
  rowClassName,
  emptyText,
  searchBtnAfterCustomActions,
  deleteText,
  exportHiddenCols,
  ...rest
}) => {
  const { docHeight } = useScreen();
  const _mobileCols = __mobileCols.filter((e) => e);
  const cols = _cols.filter((e) => e);
  const mobileColIndex = cols.length > 1 ? _mobileColIndex : 0;
  /**
   * Al pasar la prop pagination, es requerido matar
   * paginationCallback y el count de la paginación
   */
  const ref = useRef();
  const screens = Grid.useBreakpoint();
  const mobileCols = _mobileCols.length ? _mobileCols : [cols[mobileColIndex]];
  const [visible, setVisible] = useState(false);
  const [visibleModal, setVisibleModal] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [tableHeight, setTableheight] = useState(0);
  const [lastDataSource, setLastDataSource] = useState([]);
  const [focusedInput, setFocusedInput] = useState();
  // eslint-disable-next-line max-len
  const [filters, setFilters] = useState([...cols.map((col) => ({ prop: col.filterName || col.key, value: null }))]);
  const [visibleSarch, setVisibleSearch] = useState(defaultVisibleSearch);
  const [componentKey, setComponentKey] = useState();
  const [page, setPage] = useState(1);

  // normalize/validate params
  const allowExpand = _allowExpand && !!controls?.onClickExpand;
  const allowAdd = _allowAdd && !!controls?.onClickAdd;
  const allowEdit = _allowEdit && !!controls?.onClickEdit;
  const allowDelete = _allowDelete && !!controls?.onClickDelete;
  const allowExport = baseURI && (permission?.export || !permission);
  const allowImport = baseURI && (permission?.import || !permission);

  if (expandable && typeof expandable === 'object') {
    expandable.expandRowByClick = true;
  }

  const onRow = _onRow || ((record) => ({
    onClick: () => {
      if (handleOnRowClick && !rowSelection?.getCheckboxProps?.(record)?.disabled) {
        handleOnRowClick(record);
      }
    },
    onDoubleClick: () => {
      if (rowSelection?.getCheckboxProps?.(record)?.disabled) return;
      if (controls?.onClickExpand) {
        controls.onClickExpand();
      } else if (controls?.onClickEdit && allowEdit && permission?.change) {
        controls.onClickEdit();
      } else if (controls?.onClickConfig && allowConfig && permission?.change) {
        controls.onClickConfig();
      }
    },
  }));

  const SortControls = (prop, onAscend, onDescend) => (
    <Col id={`sort-controls-${prop}`}>
      <CaretUpOutlined onClick={onAscend} />
      <CaretDownOutlined onClick={onDescend} />
    </Col>
  );

  const highlight = (prop, ascend = false) => {
    const controlsCont = document.getElementById(`sort-controls-${prop}`);
    const up = controlsCont.querySelector('.anticon-caret-up');
    const down = controlsCont.querySelector('.anticon-caret-down');
    // Clear all active styles
    const all = document.querySelectorAll('.anticon-caret-up, .anticon-caret-down');
    all.forEach((element) => {
      element.classList.remove('active');
    });
    // Set active style
    if (ascend) {
      up.classList.add('active');
    } else {
      down.classList.add('active');
    }
  };

  const sortData = (prop, ascend = true) => {
    let sortedData;
    const dataClone = [...dataSource];
    if (ascend) {
      sortedData = dataClone.sort((a, b) => {
        if (a[prop] !== null) {
          return a[prop].toString().localeCompare(b[prop]);
        }
        return false;
      });
      highlight(prop, true);
    } else {
      sortedData = dataClone.sort((a, b) => {
        if (b[prop] !== null) {
          return b[prop].toString().localeCompare(a[prop]);
        }
        return false;
      });
      highlight(prop);
    }
    setDataSource(sortedData);
  };

  let filtNested = [];

  const filter = (array, fn) => array.reduce((r, o) => {
    const children = filter(o[childrenProp] || [], fn);
    if (fn(o) || children.length) {
      r.push({ ...o });
      if (!o[childrenProp]) {
        filtNested.push({ ...o });
      }
    }
    return r;
  }, []);

  const filterNestedData = () => {
    const dataClone = [...data];
    const filterList = filters.filter((item) => !!item.value);
    filtNested = [];
    if (filterList.length) {
      filterList.forEach(({ prop, value }) => {
        filter(dataClone, (i) => (i[prop] || '').toString().toLowerCase().includes(value));
      });
    }
    if (setDataSource && filtNested.length) {
      setDataSource(filtNested);
    } else {
      setDataSource(data);
    }
  };

  useEffect(() => {
    if (focusedInput && (!dataSource.length || (!lastDataSource.length && dataSource.length))) {
      const input = document.getElementById(focusedInput);
      setTimeout(() => {
        input.focus();
      });
    }
    // eslint-disable-next-line
  }, [dataSource]);

  useEffect(() => {
    if (page !== currentPage) {
      setPage(currentPage);
    }
    // eslint-disable-next-line
  }, [currentPage]);

  // useEffect(() => {
  //   setPage(1);
  // }, [_loadingTable]);

  const globalSearch = (_filters = filters) => {
    if (paginationCallback && !pagination) {
      clearTimeout(ref.current);
      const entries = _filters
        .map((e) => [e.prop, e.value?.toString().trim()])
        .filter(([, val]) => val);
      const _q = Object.fromEntries(entries);
      ref.current = setTimeout(async () => {
        setPage(1);
        await paginationCallback(1, _q);
      });
    }
  };

  const setFilter = (prop, value, catalog, search = false) => {
    let filtersClone = [...filters];
    value = typeof value === 'string' ? value : value;
    if (catalog && catalog?.use) {
      const catalogInLowerCase = catalog.cat.map((e) => ({
        ...e,
        [catalog.labelProp]: e?.[catalog.labelProp],
      }));
      catalogInLowerCase.forEach((e) => {
        if (e?.[catalog.labelProp]?.includes(value)) {
          value = e.id;
        }
      });
    }
    filtersClone = filtersClone.filter((elem) => elem.prop !== prop);
    filtersClone.push({ prop, value });
    if (search) {
      globalSearch(filtersClone);
    }
    setFilters(filtersClone);
  };

  let timeoutSearch;

  const getTitle = ({
    width,
    titleText,
    key,
    catalog,
    filterName,
    align,
    allowFilter = true,
  }) => {
    const Icon = () => (filters.find((a) => a.prop === (filterName || key)).value
      ? (
        <CloseOutlined onClick={() => setFilter((filterName || key), null, null, true)} />
      ) : <SearchOutlined />);
    return (
      <>
        <Row>
          <span className={align === 'right' ? 'head head-right' : 'head'}>
            {titleText}
          </span>
          {allowSort ? SortControls(
            (filterName || key),
            () => sortData((filterName || key)),
            () => sortData((filterName || key), false),
          ) : ''}
        </Row>
        {(allowSearch && visibleSarch) && (
          <Row>
            {catalog ? (
              <Select
                style={{ width: width - 50 }}
                labelProp={catalog.labelProp}
                onChange={(val) => {
                  setFilter((filterName || key), val, catalog, true);
                }}
                allowClear={false}
                value={filters.find((a) => a.prop === (filterName || key)).value}
                dataSource={catalog.cat}
              />
            ) : (
              <Input
                disabled={_loadingTable || !allowFilter}
                id={`input-${(filterName || key)}`}
                placeholder=""
                value={filters.find((a) => a.prop === (filterName || key)).value}
                onChange={(e) => setFilter((filterName || key), e?.target?.value)}
                onPressEnter={() => {
                  if (timeoutSearch) {
                    clearTimeout(timeoutSearch);
                  }
                  timeoutSearch = setTimeout(() => {
                    globalSearch();
                  });
                }}
                onFocus={() => setFocusedInput(`input-${(filterName || key)}`)}
              />
            )}
            <Icon />
          </Row>
        )}
      </>
    );
  };

  useEffect(() => {
    setTableheight(docHeight - 386 - restHeigth);
    setComponentKey(new Date().getTime());
    // eslint-disable-next-line
  }, [docHeight, restHeigth]);

  useEffect(() => {
    setDataSource(data);
    // eslint-disable-next-line
  }, [data]);

  const mobileColumns = mobileCols?.length > 1
    ? mobileCols.map((c) => ({
      ...c,
      key: c.dataIndex,
      title: c.titleText,
      width: c.width,
      render: c.catalog ? (val) => c.catalog.cat
        .find((e) => e.id === val)?.[c.catalog.labelProp || 'descripcion'] : c.render || null,
    }))
    : mobileCols.map((c) => ({
      ...c,
      key: c.dataIndex,
      title: c.titleText,
      width: '100%',
      render: c.catalog ? (val) => c.catalog.cat
        .find((e) => e.id === val)?.[c.catalog.labelProp || 'descripcion'] : c.render || null,
    }));

  const [selectedCols, setSelectedCols] = useState(cols.map(({ key }) => key));

  const columns = cols.map((col) => ({
    ...col,
    key: col.dataIndex,
    title: getTitle(col),
    render: col.catalog
      ? (val) => col.catalog.cat?.find((e) => e.id === val)?.[col.catalog.labelProp || 'descripcion']
      : col.render || ((val) => val),
  }));

  const filterData = () => {
    setLastDataSource(dataSource);
    const dataClone = filtNested.length ? [...filtNested] : [...data];
    let filtered = dataClone;
    const filterList = filters.filter((item) => !!item.value);
    if (filterList.length) {
      filterList.forEach(({ prop, value }) => {
        filtered = filtered.filter((item) => {
          if (item[prop] && item[prop] !== null) {
            const col = columns.find((e) => e.key === prop);
            const renderContent = col?.render?.(item[prop], item);
            return (['string', 'number'].includes(typeof renderContent) ? renderContent : item[prop])
              .toString().toLowerCase().includes(value);
          }
          return false;
        });
      });
    }
    if (setDataSource) {
      setDataSource(filtered);
    }
  };

  useEffect(() => {
    if (filterNested) {
      filterNestedData();
      filterData();
    } else {
      filterData();
    }
    // eslint-disable-next-line
  }, [filters]);

  const onExport = (type) => {
    if (type !== 'xlsx') {
      logger(type);
      return;
    }
    const visibleCols = cols.filter((col) => selectedCols.includes(col.key));
    const visibleHeaders = visibleCols.map(
      (col) => col.titleText || String(col.dataIndex || col.key || ''),
    );
    const hiddenHeaders = exportHiddenCols.map((col) => col.titleText);
    const headers = [...visibleHeaders, ...hiddenHeaders];
    const rows = dataSource.map((record) => {
      const visibleCells = visibleCols.map((col) => {
        const rawVal = col.dataIndex != null ? record[col.dataIndex] : undefined;
        if (col.catalog) {
          const match = col.catalog.cat?.find((e) => e.id === rawVal);
          return match?.[col.catalog.labelProp || 'descripcion'] ?? rawVal ?? '';
        }
        if (col.render) {
          const rendered = col.render(rawVal, record);
          if (typeof rendered === 'string' || typeof rendered === 'number') return rendered;
        }
        return rawVal ?? '';
      });
      const hiddenCells = exportHiddenCols.map((col) => col.getValue(record) ?? '');
      return [...visibleCells, ...hiddenCells];
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Datos');
    XLSX.writeFile(wb, `exportacion_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportMenu = (
    <Menu>
      <Menu.Item key="export_xlsx" onClick={() => onExport('xlsx')}>
        <FileExcelOutlined />
        Excel
      </Menu.Item>
      <Menu.Item key="export_pdf" onClick={() => onExport('pdf')}>
        <FilePdfOutlined />
        PDF
      </Menu.Item>
    </Menu>
  );

  const onGetTemplate = async () => {
    try {
      const resSign = await API.get(`${baseURI}carga/sign/`);
      const { sign } = resSign.data;
      window.open(`${baseURL}${baseURI}carga/plantilla?sign=${sign}`, '_blank');
    } catch (err) {
      onError(err);
    }
  };

  const importMenu = (
    <Menu>
      <Menu.Item key="import_get_plantilla" onClick={onGetTemplate}>
        <DownloadOutlined />
        Obtener plantilla
      </Menu.Item>
      <Menu.Item key="import_load" onClick={() => setVisibleModal(true)}>
        <UploadOutlined />
        Cargar
      </Menu.Item>
    </Menu>
  );

  const scrollX = useMemo(() => {
    const filtered = (!screens.md && mobileColumns.length > 1 ? mobileColumns : columns)
      .filter((col) => col.width && selectedCols.includes(col.key));
    const sum = filtered.reduce((prev, next) => prev + next.width, 0);
    return sum;
  }, [selectedCols, screens]);

  const content = () => {
    const options = columns.map(({ key, titleText }) => ({
      key,
      text: titleText,
    }));
    return (
      <div style={{ maxWidth: 350 }}>
        <Text>
          Seleccione las columnas que desea mostrar
        </Text>
        <Select
          dataSource={options}
          mode="multiple"
          style={{ width: '100%' }}
          value={selectedCols}
          labelProp="text"
          valueProp="value"
          onChange={(val) => {
            if (val.length >= 1) {
              setSelectedCols(val);
            }
          }}
        />
      </div>
    );
  };

  const hasAllViewPermission = _hasAllViewPermission
    || permission?.hasAllViewPermission || !permission;

  const ButtonWrapper = ({
    children,
    hasPermission,
    needKey,
    tooltip,
  }) => {
    if (tooltip && ((needKey && rowSelection?.selectedRowKeys.length) || !needKey)) {
      return (
        <Tooltip title={tooltip}>
          {children}
        </Tooltip>
      );
    }
    if (hasPermission && hasAllViewPermission) {
      if ((needKey && rowSelection?.selectedRowKeys.length) || !needKey) {
        return children;
      }
      return (
        <Tooltip title="Seleccione un elemento para realizar esta acción">
          {children}
        </Tooltip>
      );
    }

    return (
      <Tooltip title="No tienes los permisos necesarios para realizar esta acción">
        {children}
      </Tooltip>
    );
  };

  ButtonWrapper.propTypes = {
    children: PropTypes.node.isRequired,
    hasPermission: PropTypes.bool,
    needKey: PropTypes.bool,
    tooltip: PropTypes.string,
  };

  ButtonWrapper.defaultProps = {
    hasPermission: false,
    needKey: false,
    tooltip: null,
  };

  const TableControls = () => (
    <Row
      align="middle"
      justify="space-between"
      style={{ paddingBottom: 10 }}
      className="table-controls"
    >
      <div>
        {(allowColumnSelector && screens.md) && (
          <Popover
            visible={visible}
            placement="bottomRight"
            content={content}
            title={(
              <Row
                align="middle"
                justify="space-between"
              >
                <Text className="title">
                  Selector de columnas
                </Text>
                <Button type="link" onClick={() => setVisible(false)}>
                  <ArrowLeftOutlined />
                  Cancelar
                </Button>
              </Row>
            )}
            trigger="click"
          >
            <Button type="link" onClick={() => setVisible(true)}>
              <TableOutlined />
              Selector de columnas
            </Button>
          </Popover>
        )}
      </div>
      <div style={{ marginLeft: 'auto' }}>
        {allowConfig && (
          <Button
            onClick={() => controls.onClickConfig()}
            disabled={!rowSelection?.selectedRowKeys.length}
            type="link"
          >
            <SettingOutlined />
            {screens.md && 'Configurar'}
          </Button>
        )}
        {allowAdd && (
          <ButtonWrapper hasPermission={(hasAllViewPermission && permission?.add)}>
            <Button
              onClick={() => controls.onClickAdd()}
              disabled={!(hasAllViewPermission && permission?.add)}
              type="link"
            >
              <PlusOutlined />
              {screens.md && 'Agregar'}
            </Button>
          </ButtonWrapper>
        )}
        {allowEdit && (
          <ButtonWrapper
            hasPermission={hasAllViewPermission && permission?.change}
            needKey
            tooltip={(editDisabled && editDisabledTooltip) ? editDisabledTooltip : null}
          >
            <Button
              disabled={!(rowSelection?.selectedRowKeys.length
                && hasAllViewPermission && permission?.change) || editDisabled}
              onClick={() => controls.onClickEdit()}
              type="link"
            >
              <EditOutlined />
              {screens.md && editText}
            </Button>
          </ButtonWrapper>
        )}
        {allowDelete && (
          <ButtonWrapper hasPermission={permission?.delete} needKey>
            <Button
              disabled={!(rowSelection?.selectedRowKeys.length && permission?.delete)}
              onClick={() => {
                controls.onClickDelete();
                setTimeout(() => {
                  const submitBtnDelete = document.querySelector('.submit-delete');
                  if (submitBtnDelete) {
                    submitBtnDelete.focus();
                  }
                });
              }}
              type="link"
            >
              <DeleteOutlined />
              {screens.md && deleteText}
            </Button>
          </ButtonWrapper>
        )}
        {allowExpand && (
          <Button
            disabled={!rowSelection?.selectedRowKeys.length}
            onClick={() => controls.onClickExpand()}
            type="link"
          >
            <ExpandOutlined />
            {screens.md && 'Vista completa'}
          </Button>
        )}
        {allowExport && (
          <Dropdown
            overlay={exportMenu}
            placement="top"
            style={{
              heigth: 32,
            }}
          >
            <Button
              style={{
                padding: '4px 15px',
              }}
              type="link"
              onClick={(e) => e.preventDefault()}
            >
              <DownloadOutlined />
              {screens.md && 'Exportar'}
            </Button>
          </Dropdown>
        )}
        {allowImport && (
          <Dropdown
            overlay={importMenu}
            placement="top"
            style={{
              heigth: 32,
            }}
          >
            <Button
              style={{
                padding: '4px 15px',
              }}
              type="link"
              onClick={(e) => e.preventDefault()}
            >
              <UploadOutlined />
              {screens.md && 'Importar'}
            </Button>
          </Dropdown>
        )}
        {allowSearch && !searchBtnAfterCustomActions && (
          <Button
            onClick={() => setVisibleSearch(!visibleSarch)}
            type="link"
          >
            <SearchOutlined />
            {screens.md && 'Búsqueda'}
          </Button>
        )}
        {customActions.map((item) => {
          if ('hasPermission' in item) {
            return item;
          }
          if ('permission' in item) {
            return {
              ...item,
              hasPermission: hasActionPermission(item.permission?.model, item.permission?.action),
            };
          }
          return { ...item, hasPermission: true };
        }).map(({ disabledBySelection = true, ...item }) => {
          const isDisabled = (disabledBySelection && !rowSelection?.selectedRowKeys.length)
            || item.disabled || !item.hasPermission;
          const button = (
            <Button
              disabled={isDisabled}
              onClick={item.menu ? (e) => e.preventDefault() : item.onClick}
              type="link"
              className={item.className || ''}
              href={item.href}
              target={item.target || undefined}
            >
              {item.icon}
              {screens.md && item.text}
            </Button>
          );
          return (
            <ButtonWrapper
              hasPermission={item.hasPermission}
              needKey={disabledBySelection}
              tooltip={item.tooltip}
              key={item.text}
            >
              {(item.menu && !isDisabled) ? (
                <Dropdown overlay={item.menu} placement="top">
                  {button}
                </Dropdown>
              ) : button}
            </ButtonWrapper>
          );
        })}
        {allowSearch && searchBtnAfterCustomActions && (
          <Button
            onClick={() => setVisibleSearch(!visibleSarch)}
            type="link"
          >
            <SearchOutlined />
            {screens.md && 'Búsqueda'}
          </Button>
        )}
      </div>
    </Row>
  );

  const rules = {
    required: [{ required: true }],
  };

  const ModalUpload = () => {
    const [formCarga] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const onCancel = () => {
      setVisibleModal(false);
      formCarga.resetFields();
    };

    const onUpload = async () => {
      try {
        await formCarga.validateFields();
        const { file } = formCarga.getFieldsValue();
        if (file) {
          setLoading(true);
          const formData = new FormData();
          formData.append('file', file.file);
          const response = await API.post(`${baseURL}${baseURI}carga/`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          if (response?.status === 200) {
            onSuccess(response, null, 5);
            onCancel();
          }
        }
      } catch (err) {
        onError(err, setLoading);
      }
    };

    return (
      <Modal
        visible={visibleModal}
        title={(
          <Row justify="space-between">
            Carga masiva
            <FormSubmitControls onFinish={onUpload} onCancel={onCancel} loading={loading} />
          </Row>
        )}
        onCancel={onCancel}
        footer={null}
        closable={false}
        forceRender
        destroyOnClose
      >
        <Spin tip="Cargando..." spinning={loading}>
          <Form
            layout="vertical"
            form={formCarga}
            name={`carga_${componentKey}`}
            onFinish={onUpload}
          >
            <Col span={24}>
              <Dragger
                label="Archivo"
                limitMB={30}
                rules={rules.required}
                name="file"
                allowedExtensions={['xlsx']}
              />
            </Col>
          </Form>
        </Spin>
      </Modal>
    );
  };

  const _expandable = {
    // expandedRowRender: expandable?.expandedRowRender,
    childrenColumnName: childrenProp,
    indentSize: 25,
    expandIcon: ({ expanded, onExpand, record }) => (record.children && (
      expanded ? (
        <DownOutlined onClick={(e) => {
          e.stopPropagation();
          onExpand(record, e);
        }}
        />
      ) : (
        <RightOutlined onClick={(e) => {
          e.stopPropagation();
          onExpand(record, e);
        }}
        />
      )
    )),
    expandRowByClick: true,
  };

  const locale = {
    emptyText: emptyText || (hasAllViewPermission
      ? 'No hay datos' : 'Hacen falta permisos en los catálogos relacionados a este módulo'),
  };

  const _dataSource = hasAllViewPermission ? dataSource : [];

  // const clearFilter = () => {
  //   if (ref.current) {
  //     clearTimeout(ref.current);
  //     ref.current = null;
  //     return true;
  //   }
  //   return false;
  // };

  const _loading = typeof _loadingTable === 'boolean'
    ? _loadingTable : { tip: _loadingTable.tip || 'Cargando...', spinning: _loadingTable.spinning };

  const sharedProps = {
    size: 'small',
    dataSource: _dataSource,
    pagination: pagination && { position: ['bottomLeft'], pageSize },
    rowSelection: disabled ? null : rowSelection,
    onRow: disabled ? null : onRow,
    expandRowByClick: true,
    rowKey,
    locale,
    loading: _loading,
    rowClassName,
  };

  if (screens.md || !mobileColumns.length || mobileColumns.length > 1
    || expandable || expandable === false) {
    return (
      <React.Fragment key={componentKey}>
        {(!noControls && !disabled) && (<TableControls />)}
        <AntTable
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...sharedProps}
          columns={(!screens.md && mobileColumns.length > 1 ? mobileColumns : columns)
            .filter((col) => selectedCols.includes(col.key))}
          scroll={{
            y: screens.md ? tableHeight : undefined,
            x: !Number.isNaN(scrollX) ? scrollX : 0,
            scrollToFirstRowOnChange: true,
          }}
          expandable={typeof expandable === 'object' || typeof expandable === 'boolean'
            ? expandable || null : _expandable}
          className={`custom-table ${rowSelection && !disabled ? 'selectable' : ''}`}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...rest}
        />
        {(!pagination) && (
          <Pagination
            page={currentPage}
            setPage={setPage}
            callback={(p) => {
              // const cleared = clearFilter();
              // const newValue = prevFilters[prevFilters
              //   .length - 1].prop === filters[filters.length - 1].prop
              //   ? prevFilters[prevFilters.length - 1].value : null;
              // const filtersClone = cleared ? [...filters].map((e, idx) => ({
              //   ...e,
              //   value: idx === filters.length - 1 && cleared ? newValue : e.value,
              // })) : [...filters];
              // if (cleared) {
              //   setPrevFilters(filtersClone);
              //   setFilters(filtersClone);
              // }
              const entries = filters.map((e) => [e.prop, e.value]).filter(([, val]) => val);
              const _q = Object.fromEntries(entries);
              paginationCallback(p, _q, true);
            }}
            count={count}
          />
        )}
        {allowImport && <ModalUpload />}
      </React.Fragment>
    );
  }

  const displayRender = (column, record) => {
    let output = record[column.dataIndex];
    if (column.catalog) {
      const { catalog, dataIndex } = column;
      output = catalog.cat.find((e) => e.id === record[dataIndex])?.[catalog.labelProp];
    } else if (column.render) {
      output = column.render(record[column.dataIndex], record);
    }
    return output;
  };

  return (
    <React.Fragment key={componentKey}>
      {(!noControls && !disabled) && (<TableControls />)}
      <AntTable
          // eslint-disable-next-line react/jsx-props-no-spreading
        {...sharedProps}
        columns={mobileColumns}
        // scroll={{ y: tableHeight, scrollToFirstRowOnChange: true }}
        className={`custom-table expandable-responsive-table ${rowSelection && disabled ? 'selectable' : ''}`}
        expandable={{
          expandedRowRender: (record) => cols
            .filter((c) => !c.hide)
            .map((column) => (
              <Row
                key={column.key}
                justify="space-between"
              >
                <b>
                  {`${column.titleText}:`}
                </b>
                <span>
                  {displayRender(column, record)}
                </span>
              </Row>
            )),
          expandIcon: ({ expanded, onExpand, record }) => (record && (
            expanded ? (
              <DownOutlined onClick={(e) => {
                e.stopPropagation();
                onExpand(record, e);
              }}
              />
            ) : (
              <RightOutlined onClick={(e) => {
                e.stopPropagation();
                onExpand(record, e);
              }}
              />
            )
          )),
        }}
          // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      />
      {(!pagination) && (
        <Pagination
          page={page}
          setPage={setPage}
          count={count}
          callback={(p) => {
            const entries = filters.map((e) => [e.prop, e.value]).filter(([, val]) => val);
            const _q = Object.fromEntries(entries);
            paginationCallback(p, _q, true);
          }}
        />
      )}
    </React.Fragment>
  );
};

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  cols: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({}), PropTypes.bool])).isRequired,
  mobileCols: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({}), PropTypes.bool])),
  rowSelection: PropTypes.shape({
    selectedRowKeys: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.number),
      PropTypes.arrayOf(PropTypes.string),
    ]),
    type: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    preserveSelectedRowKeys: PropTypes.bool,
    getCheckboxProps: PropTypes.func,
  }),
  editText: PropTypes.string,
  noControls: PropTypes.bool,
  controls: PropTypes.shape({
    onClickAdd: PropTypes.func,
    onClickEdit: PropTypes.func,
    onClickExpand: PropTypes.func,
    onClickDelete: PropTypes.func,
    onClickConfig: PropTypes.func,
  }),
  onRow: PropTypes.func,
  allowEdit: PropTypes.bool,
  editDisabled: PropTypes.bool,
  editDisabledTooltip: PropTypes.string,
  allowAdd: PropTypes.bool,
  allowDelete: PropTypes.bool,
  allowSearch: PropTypes.bool,
  allowSort: PropTypes.bool,
  allowConfig: PropTypes.bool,
  allowExpand: PropTypes.bool,
  expandable: PropTypes.oneOfType([PropTypes.node, PropTypes.bool, PropTypes.shape()]),
  filterNested: PropTypes.bool,
  childrenProp: PropTypes.string,
  rowKey: PropTypes.string,
  customActions: PropTypes.arrayOf(PropTypes.shape({
    onClick: PropTypes.func,
    icon: PropTypes.node.isRequired,
    text: PropTypes.string.isRequired,
    tooltip: PropTypes.string,
    disabled: PropTypes.bool,
    // Al pasar menu, el botón despliega un Dropdown en lugar de ejecutar onClick
    menu: PropTypes.node,
    permission: PropTypes.shape({
      model: PropTypes.string,
      action: PropTypes.string,
    }),
    disabledBySelection: PropTypes.bool,
  })),
  handleOnRowClick: PropTypes.func,
  mobileColIndex: PropTypes.number,
  pageSize: PropTypes.number,
  disabled: PropTypes.bool,
  allowColumnSelector: PropTypes.bool,
  baseURI: PropTypes.string,
  permission: PropTypes.shape({
    add: PropTypes.bool,
    authorize: PropTypes.bool,
    cancel: PropTypes.bool,
    change: PropTypes.bool,
    delete: PropTypes.bool,
    export: PropTypes.bool,
    import: PropTypes.bool,
    sync: PropTypes.bool,
    view: PropTypes.bool,
    model: PropTypes.string,
    hasAllViewPermission: PropTypes.bool,
    hasPermission: PropTypes.bool,
  }),
  hasAllViewPermission: PropTypes.bool,
  pagination: PropTypes.bool,
  paginationCallback: PropTypes.func,
  count: PropTypes.number,
  currentPage: PropTypes.number,
  defaultVisibleSearch: PropTypes.bool,
  loading: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    tip: PropTypes.string,
    spinning: PropTypes.bool,
  })]),
  restHeigth: PropTypes.number,
  rowClassName: PropTypes.func,
  emptyText: PropTypes.string,
  searchBtnAfterCustomActions: PropTypes.bool,
  deleteText: PropTypes.string,
  exportHiddenCols: PropTypes.arrayOf(PropTypes.shape({
    titleText: PropTypes.string.isRequired,
    getValue: PropTypes.func.isRequired,
  })),
};

Table.defaultProps = {
  mobileCols: [],
  data: [],
  editText: 'Editar',
  deleteText: 'Eliminar',
  noControls: false,
  controls: null,
  rowSelection: null,
  onRow: null,
  allowEdit: true,
  editDisabled: false,
  editDisabledTooltip: null,
  allowAdd: true,
  allowDelete: true,
  allowSearch: true,
  allowSort: true,
  allowConfig: false,
  allowExpand: false,
  expandable: null,
  filterNested: false,
  childrenProp: null,
  rowKey: 'key',
  customActions: [],
  handleOnRowClick: null,
  mobileColIndex: 1,
  pageSize: 10,
  disabled: false,
  allowColumnSelector: true,
  baseURI: null,
  permission: null,
  hasAllViewPermission: false,
  pagination: true,
  paginationCallback: null,
  count: 0,
  currentPage: 1,
  defaultVisibleSearch: false,
  loading: false,
  restHeigth: 0,
  rowClassName: null,
  emptyText: null,
  searchBtnAfterCustomActions: false,
  exportHiddenCols: [],
};

export default Table;
